import React from "react";

import { formatDateOnly } from "../../../utils/datetime";
import { tenantDisplayName } from "../../../utils/tenantDisplayName";
import PersonCell from "./PersonCell";
import TenancyRowActions from "./TenancyRowActions";

/**
 * The "Ohne Mietverhältnis" block: people the organisation has recorded who
 * hold no tenancy at all.
 *
 * A SEPARATE section with its OWN columns, deliberately not a `TenantUnitGroup`
 * with blanks in it. That component's seven columns — Co-Mieter, Mietverhältnis,
 * Einzug/Auszug, Monatl. Ertrag, Status — are facts about a CONTRACT. For a
 * person who holds none they are not empty, they are meaningless, and a row of
 * em dashes under "Status" reads as an assertion that the contract has no
 * status rather than that there is no contract. Four honest columns say more
 * than seven hollow ones.
 *
 * A row here is a PERSON, unlike every other row on this page, where a row is a
 * tenancy. That is why the block is visually its own thing rather than another
 * unit card: the reader has to know the noun changed.
 *
 * Renders nothing at all when the query came back empty. An organisation where
 * every tenant holds a contract sees the page exactly as it was before this
 * block existed — a permanent "0 Mieter" header would be noise on the common
 * case. An error DOES render, so a failed load can never be mistaken for "there
 * are none".
 */

/** Both layouts render the same fields — the narrow one is a re-flow, not a cut. */
function ContactCell({ tenant }) {
  const email = String(tenant?.email || "").trim();
  const phone = String(tenant?.phone || "").trim();
  if (!email && !phone) {
    return <span className="text-[12.5px] text-muted-foreground">—</span>;
  }
  return (
    <div className="min-w-0">
      {email ? (
        <div className="truncate text-[12.5px] text-foreground">{email}</div>
      ) : null}
      {phone ? (
        <div className="truncate text-[11px] text-muted-foreground">{phone}</div>
      ) : null}
    </div>
  );
}

/**
 * Name only. The e-mail and phone live in their own column, so passing them
 * here as well would print the same two lines twice in one row.
 */
function personFor(tenant) {
  return {
    tenantId: tenant?.id != null ? String(tenant.id) : null,
    name: tenantDisplayName(tenant),
    email: null,
    phone: null,
    roleLabel: null,
  };
}

export default function UnassignedTenantsSection({
  loading,
  error,
  items,
  total,
  skip,
  pageSize,
  onPageChange,
  onOpenTenant,
  buildActions,
}) {
  const rows = Array.isArray(items) ? items : [];

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-[10px] border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-700 dark:text-rose-300"
      >
        <strong className="font-semibold">
          Mieter ohne Mietverhältnis konnten nicht geladen werden:
        </strong>{" "}
        {error}
      </div>
    );
  }

  // Nothing to say yet, and nothing to say at all, look the same on purpose:
  // no skeleton flashes on a page whose usual answer is "none".
  if (rows.length === 0) return null;

  const page = pageSize > 0 ? Math.floor(skip / pageSize) : 0;
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));
  const paginated = totalPages > 1;

  return (
    <section
      className="overflow-hidden rounded-[10px] border border-amber-500/30 bg-card"
      aria-busy={loading ? "true" : undefined}
      data-testid="unassigned-tenants-section"
    >
      <div className="flex flex-wrap items-center gap-[10px] border-b border-border bg-amber-500/[0.07] px-[14px] py-[10px]">
        <div className="min-w-0">
          <h2 className="m-0 truncate text-[14px] font-semibold text-foreground">
            Ohne Mietverhältnis
          </h2>
          <p className="m-0 text-[11.5px] text-muted-foreground">
            Erfasste Mieter, die noch keinem Mietverhältnis zugeordnet sind
          </p>
        </div>
        <span className="ml-auto whitespace-nowrap rounded-full border border-border bg-card px-[9px] py-[2px] text-[11px] text-muted-foreground">
          {total} Mieter
        </span>
      </div>

      {/* Desktop: the full table. */}
      <div
        className="hidden overflow-x-auto lg:block"
        data-testid="unassigned-tenants-table"
      >
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-border">
              {["Mieter", "Kontakt", "Erfasst am", ""].map((h, i) => (
                <th
                  key={h || `actions-${i}`}
                  scope="col"
                  className="whitespace-nowrap px-[14px] py-[8px] text-[11px] font-medium text-muted-foreground"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr
                key={String(t.id)}
                onClick={() => onOpenTenant(t)}
                className="cursor-pointer border-b border-border/70 last:border-b-0 hover:bg-muted/50"
              >
                <td className="px-[14px] py-[10px] align-top">
                  <PersonCell person={personFor(t)} />
                </td>
                <td className="px-[14px] py-[10px] align-top">
                  <ContactCell tenant={t} />
                </td>
                <td className="whitespace-nowrap px-[14px] py-[10px] align-top text-[12.5px] tabular-nums text-muted-foreground">
                  {formatDateOnly(t.created_at)}
                </td>
                <td className="px-[14px] py-[10px] align-top text-right">
                  <TenancyRowActions items={buildActions(t)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Narrow screens: one card per person, same fields. */}
      <div className="flex flex-col lg:hidden" data-testid="unassigned-tenants-cards">
        {rows.map((t) => (
          <div
            key={String(t.id)}
            onClick={() => onOpenTenant(t)}
            className="cursor-pointer border-b border-border/70 px-[14px] py-[12px] last:border-b-0 hover:bg-muted/50"
          >
            <div className="flex items-start justify-between gap-2">
              <PersonCell person={personFor(t)} />
              <TenancyRowActions items={buildActions(t)} />
            </div>
            <div className="mt-[8px] flex flex-wrap items-center justify-between gap-2">
              <ContactCell tenant={t} />
              <span className="whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
                Erfasst am {formatDateOnly(t.created_at)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {paginated ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-[14px] py-[8px] text-[12.5px] text-muted-foreground">
          <span>
            Seite {page + 1} von {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(0, page - 1))}
              disabled={page === 0}
              className="rounded-[7px] border border-border bg-card px-[12px] py-[5px] text-foreground disabled:opacity-40 hover:bg-muted"
            >
              Zurück
            </button>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-[7px] border border-border bg-card px-[12px] py-[5px] text-foreground disabled:opacity-40 hover:bg-muted"
            >
              Weiter
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
